import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    positionKey,
    positionsEqual,
    parsePositionKey,
    edgeKey,
    edgeAdjacentCells
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';
import { stripSeparators } from '../stateSerialization.js';

export interface StemPosition {
    side: 'top' | 'bottom' | 'left' | 'right';
    index: number;  // 0 to GRID_SIZE-1
}

export class BranchesGame {
    protected grid: Grid;
    protected stem: StemPosition | null = null;
    protected segments: Set<string> = new Set();

    // Computed state (updated after each change)
    protected connectedSegments: Set<string> = new Set();
    protected disconnectedSegments: Set<string> = new Set();
    protected distances: Map<string, number> = new Map();  // positionKey -> distance
    protected branchingViolations: Set<string> = new Set();  // positionKeys of invalid branch points

    // Antenna-style build: a branch being drawn out of a digit, one aligned run
    // at a time, committed live but reversible until it reaches another digit.
    private buildAnchor: Position | null = null;
    private buildTip: Position | null = null;
    private buildPath: Position[] = [];
    private buildAdded: string[] = [];     // edgeKeys added during this build
    private buildRemoved: string[] = [];   // edgeKeys removed during this build (for cancel)
    private buildDirDecided = false;

    constructor(grid: Grid) {
        this.grid = grid;
    }

    protected get gg() { return this.grid.geometry; }
    protected get gs() { return this.grid.serializer; }

    getGrid(): Grid {
        return this.grid;
    }

    getStem(): StemPosition | null {
        return this.stem;
    }

    /**
     * Get the grid cell that the stem enters.
     */
    getStemEntryCell(): Position | null {
        if (!this.stem) return null;

        switch (this.stem.side) {
            case 'top':
                return { row: 0, col: this.stem.index };
            case 'bottom':
                return { row: this.grid.size - 1, col: this.stem.index };
            case 'left':
                return { row: this.stem.index, col: 0 };
            case 'right':
                return { row: this.stem.index, col: this.grid.size - 1 };
        }
    }

    getSegments(): Set<string> {
        return new Set(this.segments);
    }

    getConnectedSegments(): Set<string> {
        return new Set(this.connectedSegments);
    }

    getDisconnectedSegments(): Set<string> {
        return new Set(this.disconnectedSegments);
    }

    getDistance(pos: Position): number | null {
        const key = positionKey(pos);
        const dist = this.distances.get(key);
        return dist !== undefined ? dist : null;
    }

    /**
     * Get branch endpoints (tips) - cells with exactly 1 segment connection
     * that are connected to the stem, excluding the stem entry cell itself.
     * Returns array of [position, distance] pairs.
     */
    getEndpoints(): Array<[Position, number]> {
        const adjacency = this.buildAdjacencyGraph();
        const result: Array<[Position, number]> = [];

        const stemCell = this.getStemEntryCell();
        const stemKey = stemCell ? positionKey(stemCell) : null;

        for (const [key, neighbors] of adjacency) {
            // Endpoint has exactly 1 connection and is not the stem entry cell
            if (neighbors.length === 1 && key !== stemKey) {
                const distance = this.distances.get(key);
                if (distance !== undefined) {
                    result.push([parsePositionKey(key), distance]);
                }
            }
        }

        // Stem entry cell is a tip when it has 0 or 1 segment connections
        // (0 = lone stem, 1 = end of a branch)
        if (stemCell && this.stem) {
            const stemNeighbors = adjacency.get(stemKey!) || [];
            if (stemNeighbors.length <= 1) {
                const distance = this.distances.get(stemKey!) ?? 0;
                result.push([stemCell, distance]);
            }
        }

        return result;
    }

    /**
     * Get dangling endpoints: non-digit tips when all digits are correctly ordered.
     */
    getDanglingEndpoints(): Position[] {
        const correctDigits = this.getCorrectDigits();
        const digitCount = this.grid.maxDigit - this.grid.minDigit + 1;
        if (correctDigits.length !== digitCount) return [];

        const stemCell = this.getStemEntryCell();
        return this.getEndpoints()
            .filter(([pos]) => !this.grid.hasDigitAt(pos) &&
                !(stemCell && positionsEqual(pos, stemCell)))
            .map(([pos]) => pos);
    }

    /**
     * The first pair of digits the branches reach in the wrong order: the digit
     * after the correct run is connected, but sits no further from the stem than
     * the digit before it. Null while that digit simply isn't connected yet.
     */
    getOutOfOrderDigits(): [number, number] | null {
        const correct = this.getCorrectDigits();
        if (correct.length === 0) return null;

        const previous = correct[correct.length - 1];
        const next = previous + 1;
        if (next > this.grid.maxDigit) return null;

        const pos = this.grid.getDigitPosition(next);
        if (!pos) return null;
        if (this.distances.get(positionKey(pos)) === undefined) return null;

        return [previous, next];
    }

    getBranchingViolations(): Set<string> {
        return new Set(this.branchingViolations);
    }

    /**
     * Rim positions where a stem may be placed. Only meaningful before a stem
     * exists — used to hint the very first click.
     */
    getStemHintPositions(): StemPosition[] {
        if (this.stem) return [];
        const size = this.grid.size;
        const result: StemPosition[] = [];
        for (let i = 0; i < size; i++) {
            for (const side of ['top', 'bottom', 'left', 'right'] as StemPosition['side'][]) {
                const stem: StemPosition = { side, index: i };
                // The stem can't enter a digit cell, so don't hint those rim edges.
                if (this.grid.hasDigitAt(this.stemEntryCellFor(stem))) continue;
                result.push(stem);
            }
        }
        return result;
    }

    /**
     * Directed moves that would grow the stem-connected ("teal") network: from the
     * stem root, an active tip, or a connected digit (the `from`), into a cell not
     * already on the network and without creating an obvious branching violation
     * (the `to`). Used to surface newcomer hint chevrons pointing the way to grow.
     */
    getHintMoves(): Array<{ from: Position; to: Position }> {
        if (!this.stem) return [];

        const adjacency = this.buildAdjacencyGraph();
        const stemCell = this.getStemEntryCell()!;
        const stemKey = positionKey(stemCell);

        // Total degree of a cell, counting the stem's rim link at its entry cell.
        const degreeOf = (key: string): number =>
            (adjacency.get(key)?.length ?? 0) + (key === stemKey ? 1 : 0);

        // Source cells the network can grow from: the stem root always, plus any
        // connected digit (digits may branch) or connected non-digit tip.
        const sources: Position[] = [stemCell];
        for (const [key] of this.distances) {
            if (key === stemKey) continue;
            const pos = parsePositionKey(key);
            if (this.grid.hasDigitAt(pos) || degreeOf(key) <= 1) sources.push(pos);
        }

        const seen = new Set<string>();
        const hints: Array<{ from: Position; to: Position }> = [];
        for (const src of sources) {
            const srcKey = positionKey(src);
            // Non-digit cells cap at 2 connections; a full one can't grow.
            if (!this.grid.hasDigitAt(src) && degreeOf(srcKey) >= 2) continue;

            for (const dir of ['N', 'S', 'E', 'W'] as Direction[]) {
                const nb = this.grid.moveInside(src, dir);
                if (!nb) continue;
                if (this.hasSegment(src, nb)) continue;             // already placed
                const nbKey = positionKey(nb);
                if (this.distances.has(nbKey)) continue;            // already teal: would loop
                // Don't suggest a move that would over-connect a non-digit neighbour.
                if (!this.grid.hasDigitAt(nb) && degreeOf(nbKey) >= 2) continue;

                const edge = this.gg.createEdgeFromCells(src, nb);
                if (edge && !seen.has(edgeKey(edge))) {
                    seen.add(edgeKey(edge));
                    hints.push({ from: src, to: nb });
                }
            }
        }
        return hints;
    }

    /**
     * Toggle stem at the given position.
     * If stem is already at this position, remove it.
     * Otherwise, set stem to this position.
     */
    toggleStem(pos: StemPosition): void {
        if (this.stem &&
            this.stem.side === pos.side &&
            this.stem.index === pos.index) {
            this.stem = null;
        } else {
            this.stem = pos;
        }
        this.computeState();
    }

    /**
     * Check if a segment exists between two adjacent cells.
     */
    hasSegment(a: Position, b: Position): boolean {
        const edge = this.gg.createEdgeFromCells(a, b);
        if (!edge) return false;
        return this.segments.has(edgeKey(edge));
    }

    /**
     * Toggle a segment between two adjacent cells.
     * Returns true if state changed.
     */
    toggleSegment(a: Position, b: Position): boolean {
        if (!this.grid.isValidPosition(a) || !this.grid.isValidPosition(b)) return false;

        const edge = this.gg.createEdgeFromCells(a, b);
        if (!edge) return false;  // Not adjacent

        const key = edgeKey(edge);

        if (this.segments.has(key)) {
            this.segments.delete(key);
        } else {
            this.segments.add(key);
        }

        this.computeState();
        return true;
    }

    // ── Antenna-style branch building ─────────────────────────────

    isBuilding(): boolean { return this.buildAnchor !== null; }
    getBuildAnchor(): Position | null { return this.buildAnchor ? { ...this.buildAnchor } : null; }
    getBuildTip(): Position | null { return this.buildTip ? { ...this.buildTip } : null; }
    getBuildPath(): Position[] { return this.buildPath.map(p => ({ ...p })); }

    /** Begin building a branch out of a digit cell. */
    startBuild(cell: Position): boolean {
        if (this.isBuilding()) return false;
        if (!this.grid.hasDigitAt(cell)) return false;
        this.buildAnchor = { ...cell };
        this.buildTip = { ...cell };
        this.buildPath = [{ ...cell }];
        this.buildAdded = [];
        this.buildRemoved = [];
        this.buildDirDecided = false;
        return true;
    }

    /**
     * Handle a click while building. Clicking the anchor again cancels; an
     * off-line click is a no-op. The straight run from the tip toward the click
     * is laid cell by cell, stopping (and confirming) at the first digit or
     * already-segmented cell it meets — so clicking past a branch joins it.
     */
    buildClick(cell: Position): 'none' | 'extended' | 'confirmed' | 'cancelled' {
        if (!this.isBuilding()) return 'none';
        const anchor = this.buildAnchor!;
        const tip = this.buildTip!;

        if (positionsEqual(cell, anchor)) { this.cancelBuild(); return 'cancelled'; }
        // Clicking the current tip again confirms the branch as drawn.
        if (positionsEqual(cell, tip)) { this.commitBuild(); return 'confirmed'; }
        if (!this.isAlignedRun(tip, cell)) return 'none';

        // The first extension fixes the direction and clears the digit's old
        // branch. Done before the walk so the cleared cells don't count as joins.
        if (!this.buildDirDecided) {
            const dir = this.dirBetween(anchor, cell);
            if (dir) this.clearOutgoingBranch(anchor, dir);
            this.buildDirDecided = true;
        }

        const stepR = Math.sign(cell.row - tip.row);
        const stepC = Math.sign(cell.col - tip.col);
        let cur = tip;
        let extended = false;

        while (!positionsEqual(cur, cell)) {
            const next: Position = { row: cur.row + stepR, col: cur.col + stepC };
            // Join if `next` is a digit or already has a segment — checked before
            // we lay our own segment into it, so a crossing branch is detected.
            const joins = this.grid.hasDigitAt(next) || this.cellHasSegment(next);

            this.addBuildSegment(cur, next);
            this.buildPath.push({ ...next });
            this.buildTip = { ...next };
            cur = next;
            extended = true;

            if (joins) { this.commitBuild(); return 'confirmed'; }
        }

        if (!extended) return 'none';
        this.computeState();
        return 'extended';
    }

    /** Discard the in-progress branch, restoring segments to their pre-build state. */
    cancelBuild(): void {
        if (!this.isBuilding()) return;
        for (const k of this.buildAdded) this.segments.delete(k);
        for (const k of this.buildRemoved) this.segments.add(k);
        this.clearBuildState();
        this.computeState();
    }

    /** Keep the in-progress branch as placed. */
    private commitBuild(): void {
        this.clearBuildState();
        this.computeState();
    }

    private clearBuildState(): void {
        this.buildAnchor = null;
        this.buildTip = null;
        this.buildPath = [];
        this.buildAdded = [];
        this.buildRemoved = [];
        this.buildDirDecided = false;
    }

    /** Same row or column, at least one cell apart. */
    private isAlignedRun(a: Position, b: Position): boolean {
        return !positionsEqual(a, b) && (a.row === b.row || a.col === b.col);
    }

    private dirBetween(a: Position, b: Position): Direction | null {
        if (a.row === b.row) return b.col > a.col ? 'E' : 'W';
        if (a.col === b.col) return b.row > a.row ? 'S' : 'N';
        return null;
    }

    /** Whether any segment is incident to a cell. */
    private cellHasSegment(cell: Position): boolean {
        for (const d of ['N', 'S', 'E', 'W'] as Direction[]) {
            const nb = this.grid.moveInside(cell, d);
            if (nb && this.hasSegment(cell, nb)) return true;
        }
        return false;
    }

    /** Add a segment, recording it so the build can be cancelled. */
    private addBuildSegment(a: Position, b: Position): void {
        const edge = this.gg.createEdgeFromCells(a, b);
        if (!edge) return;
        const k = edgeKey(edge);
        if (this.segments.has(k)) return;
        this.segments.add(k);
        const ri = this.buildRemoved.indexOf(k);
        if (ri >= 0) this.buildRemoved.splice(ri, 1);  // re-adding one we just cleared
        else this.buildAdded.push(k);
    }

    /**
     * Remove the existing branch leaving `anchor` in direction `dir`: walk the
     * connected segments out from the anchor, deleting them, but stop at digit
     * cells so their own branches survive. Records removals for cancel.
     */
    private clearOutgoingBranch(anchor: Position, dir: Direction): void {
        const first = this.grid.moveInside(anchor, dir);
        if (!first || !this.hasSegment(anchor, first)) return;

        const removeSeg = (a: Position, b: Position): void => {
            const edge = this.gg.createEdgeFromCells(a, b);
            if (!edge) return;
            const k = edgeKey(edge);
            if (this.segments.has(k)) { this.segments.delete(k); this.buildRemoved.push(k); }
        };

        removeSeg(anchor, first);
        const visited = new Set<string>([positionKey(anchor)]);
        const stack: Position[] = [first];
        while (stack.length > 0) {
            const cur = stack.pop()!;
            const curKey = positionKey(cur);
            if (visited.has(curKey)) continue;
            visited.add(curKey);
            if (this.grid.hasDigitAt(cur)) continue;  // stop at digits; keep their branches
            for (const d of ['N', 'S', 'E', 'W'] as Direction[]) {
                const nb = this.grid.moveInside(cur, d);
                if (!nb || !this.hasSegment(cur, nb)) continue;
                removeSeg(cur, nb);
                if (!visited.has(positionKey(nb))) stack.push(nb);
            }
        }
    }

    /**
     * Compute connected segments, distances, and branching violations.
     */
    protected computeState(): void {
        this.connectedSegments.clear();
        this.disconnectedSegments.clear();
        this.distances.clear();
        this.branchingViolations.clear();

        // Build adjacency graph
        const adjacency = this.buildAdjacencyGraph();

        // Validate branching (>2 connections only allowed at digit cells)
        this.validateBranching(adjacency);

        // If no stem, all segments are disconnected
        if (!this.stem) {
            for (const seg of this.segments) {
                this.disconnectedSegments.add(seg);
            }
            return;
        }

        const stemCell = this.getStemEntryCell()!;
        const stemKey = positionKey(stemCell);

        // BFS from stem cell
        const visited = new Set<string>();
        const queue: Array<{ key: string; distance: number }> = [];

        // Start BFS if stem cell has any segments
        if (adjacency.has(stemKey)) {
            queue.push({ key: stemKey, distance: 0 });
            this.distances.set(stemKey, 0);
        }

        while (queue.length > 0) {
            const { key, distance } = queue.shift()!;
            if (visited.has(key)) continue;
            visited.add(key);

            const neighbors = adjacency.get(key) || [];
            for (const neighborKey of neighbors) {
                // Mark the segment as connected
                const pos = parsePositionKey(key);
                const neighborPos = parsePositionKey(neighborKey);
                const edge = this.gg.createEdgeFromCells(pos, neighborPos);
                if (edge) {
                    this.connectedSegments.add(edgeKey(edge));
                }

                if (!visited.has(neighborKey)) {
                    const newDist = distance + 1;
                    if (!this.distances.has(neighborKey)) {
                        this.distances.set(neighborKey, newDist);
                        queue.push({ key: neighborKey, distance: newDist });
                    }
                }
            }
        }

        // All segments not connected are disconnected
        for (const seg of this.segments) {
            if (!this.connectedSegments.has(seg)) {
                this.disconnectedSegments.add(seg);
            }
        }
    }

    /**
     * Build adjacency graph from segments.
     */
    protected buildAdjacencyGraph(): Map<string, string[]> {
        const adjacency = new Map<string, string[]>();

        for (const seg of this.segments) {
            const edge = this.grid.deserializeEdge(seg);
            if (!edge) continue;

            const cells = edgeAdjacentCells(edge);
            if (cells.length !== 2) continue;

            const keyA = positionKey(cells[0]);
            const keyB = positionKey(cells[1]);

            if (!adjacency.has(keyA)) adjacency.set(keyA, []);
            if (!adjacency.has(keyB)) adjacency.set(keyB, []);

            adjacency.get(keyA)!.push(keyB);
            adjacency.get(keyB)!.push(keyA);
        }

        return adjacency;
    }

    /**
     * Check for branching violations.
     * Cells with >2 connections that are not digit cells are violations.
     * The stem's rim-link counts as a connection at its entry cell, so a non-digit stem
     * cell may host at most one branch segment (stem + 1 = 2 connections).
     */
    protected validateBranching(adjacency: Map<string, string[]>): void {
        const stemCell = this.getStemEntryCell();
        const stemKey = stemCell ? positionKey(stemCell) : null;

        for (const [cellKey, neighbors] of adjacency) {
            const degree = neighbors.length + (cellKey === stemKey ? 1 : 0);
            if (degree > 2) {
                const pos = parsePositionKey(cellKey);

                // Branching (3+ connections) only allowed at digit cells
                if (!this.grid.hasDigitAt(pos)) {
                    this.branchingViolations.add(cellKey);
                }
            }
        }
    }

    /**
     * Get digits that satisfy the increasing distance constraint.
     * Returns digits in order (1, 2, 3, ...) as long as each has
     * a strictly greater distance than the previous.
     */
    getCorrectDigits(): number[] {
        if (!this.stem) return [];

        const correct: number[] = [];
        let previousDistance = -1;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.grid.getDigitPosition(digit);
            if (!pos) break;

            const distance = this.distances.get(positionKey(pos));
            if (distance === undefined) break;  // Not connected
            if (distance <= previousDistance) break;  // Not strictly increasing

            correct.push(digit);
            previousDistance = distance;
        }

        return correct;
    }

    /**
     * Check if the puzzle is solved.
     */
    checkWin(): boolean {
        // Must have a stem
        if (!this.stem) return false;

        // Must have no branching violations
        if (this.branchingViolations.size > 0) return false;

        // All digits must be connected and at strictly increasing distances
        let previousDistance = -1;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.grid.getDigitPosition(digit);
            if (!pos) return false;

            const distance = this.distances.get(positionKey(pos));
            if (distance === undefined) return false;  // Not connected
            if (distance <= previousDistance) return false;  // Not strictly increasing

            previousDistance = distance;
        }

        // Must have no disconnected segments
        if (this.disconnectedSegments.size > 0) return false;

        // Must have no dangling branches (non-digit endpoints, excluding stem entry)
        const stemEntry = this.getStemEntryCell();
        const endpoints = this.getEndpoints();
        for (const [pos] of endpoints) {
            if (stemEntry && positionsEqual(pos, stemEntry)) continue;
            if (!this.grid.hasDigitAt(pos)) return false;
        }

        return true;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        // Primary: Distance spread (last digit distance - first digit distance)
        const posMin = this.grid.getDigitPosition(this.grid.minDigit);
        const posMax = this.grid.getDigitPosition(this.grid.maxDigit);
        if (!posMin || !posMax) return null;

        const distMin = this.distances.get(positionKey(posMin));
        const distMax = this.distances.get(positionKey(posMax));
        if (distMin === undefined || distMax === undefined) return null;

        const distanceSpread = distMax - distMin;

        // Secondary: corner count (non-digit cells where the path turns)
        const corners = this.getCornerCells().length;

        return calculateScore(distanceSpread, corners);
    }

    /** Non-digit cells where the path turns (one horizontal + one vertical neighbor). */
    private getCornerCells(): Position[] {
        const adjacency = this.buildAdjacencyGraph();
        const corners: Position[] = [];
        for (const [cellKey, neighbors] of adjacency) {
            if (neighbors.length !== 2) continue;
            const pos = parsePositionKey(cellKey);
            if (this.grid.hasDigitAt(pos)) continue;
            const n0 = parsePositionKey(neighbors[0]);
            const n1 = parsePositionKey(neighbors[1]);
            if (n0.row !== n1.row && n0.col !== n1.col) corners.push(pos);
        }
        return corners;
    }

    /** Scoring-rules overlay: distance spread (primary) as an amber arc between the
     *  min/max digit, plus the corner cells (secondary). */
    getScoringHighlight(): ScoringHighlight {
        const from = this.grid.getDigitPosition(this.grid.minDigit);
        const to = this.grid.getDigitPosition(this.grid.maxDigit);
        if (!from || !to) return {};
        const fromValue = this.distances.get(positionKey(from)) ?? 0;
        const toValue = this.distances.get(positionKey(to)) ?? 0;
        return {
            spreadArc: { from, to, fromValue, toValue, primary: true, badgeSize: 'sm' },
            secondaryCells: this.getCornerCells(),
        };
    }

    isStarted(): boolean {
        return this.stem !== null || this.segments.size > 0;
    }

    reset(): void {
        this.stem = null;
        this.segments.clear();
        this.clearBuildState();
        this.computeState();
    }

    /**
     * Jumpstart a solution: place a stem near digit 1 and a simple branch
     * chain joining digit 1 to digit 2 (digit 2 farther from the stem).
     * The result is a valid partial — digits 1 and 2 are correctly ordered.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const d1 = this.grid.getDigitPosition(this.grid.minDigit);
        const d2 = this.grid.getDigitPosition(this.grid.minDigit + 1);
        if (!d1 || !d2) return false;

        const stem = this.chooseStemNear(d1);
        if (!stem) return false;
        const entry = this.stemEntryCellFor(stem);

        // Leg 1: stem entry → digit 1, routing around other digits.
        const leg1 = this.findCellPath(entry, d1, new Set());
        if (!leg1) return false;

        // Leg 2: digit 1 → digit 2, avoiding leg-1 cells (except the shared
        // junction at digit 1) so the result stays a simple, valid path.
        const used = new Set(leg1.map(positionKey));
        used.delete(positionKey(d1));
        const leg2 = this.findCellPath(d1, d2, used);
        if (!leg2) return false;

        // Both legs found — commit the placement.
        this.toggleStem(stem);
        this.placePath(leg1);
        this.placePath(leg2);
        return true;
    }

    /**
     * Pick a stem on the rim near a cell, preferring the closest border whose
     * entry cell isn't some other digit. Returns null if none is suitable.
     */
    private chooseStemNear(cell: Position): StemPosition | null {
        const size = this.grid.size;
        const dist: Record<StemPosition['side'], number> = {
            top: cell.row,
            bottom: size - 1 - cell.row,
            left: cell.col,
            right: size - 1 - cell.col,
        };
        const candidates: StemPosition[] = [
            { side: 'top', index: cell.col },
            { side: 'bottom', index: cell.col },
            { side: 'left', index: cell.row },
            { side: 'right', index: cell.row },
        ];
        candidates.sort((a, b) => dist[a.side] - dist[b.side]);

        const cellKey = positionKey(cell);
        for (const stem of candidates) {
            const entry = this.stemEntryCellFor(stem);
            if (positionKey(entry) === cellKey) return stem;  // entry is the cell itself
            if (!this.grid.hasDigitAt(entry)) return stem;     // entry isn't a foreign digit
        }
        return null;
    }

    /** Grid cell that a given stem enters (border cell), without setting it. */
    private stemEntryCellFor(stem: StemPosition): Position {
        const size = this.grid.size;
        switch (stem.side) {
            case 'top': return { row: 0, col: stem.index };
            case 'bottom': return { row: size - 1, col: stem.index };
            case 'left': return { row: stem.index, col: 0 };
            case 'right': return { row: stem.index, col: size - 1 };
        }
    }

    /**
     * Shortest orthogonal cell path from start to goal (inclusive), avoiding
     * cells in `blocked` and any digit cell other than start/goal. Null if none.
     */
    private findCellPath(start: Position, goal: Position, blocked: Set<string>): Position[] | null {
        const startKey = positionKey(start);
        const goalKey = positionKey(goal);

        const isBlocked = (p: Position): boolean => {
            const k = positionKey(p);
            if (k === startKey || k === goalKey) return false;
            if (blocked.has(k)) return true;
            return this.grid.hasDigitAt(p);
        };

        const prev = new Map<string, string | null>();
        prev.set(startKey, null);
        const queue: Position[] = [start];

        while (queue.length > 0) {
            const cur = queue.shift()!;
            const curKey = positionKey(cur);

            if (curKey === goalKey) {
                const path: Position[] = [];
                let k: string | null = curKey;
                while (k) {
                    path.push(parsePositionKey(k));
                    k = prev.get(k) ?? null;
                }
                return path.reverse();
            }

            for (const dir of ['N', 'S', 'E', 'W'] as Direction[]) {
                const nb = this.grid.moveInside(cur, dir);
                if (!nb) continue;
                const nbKey = positionKey(nb);
                if (prev.has(nbKey)) continue;
                if (isBlocked(nb)) continue;
                prev.set(nbKey, curKey);
                queue.push(nb);
            }
        }

        return null;
    }

    /** Lay segments between consecutive cells of a path. */
    private placePath(path: Position[]): void {
        for (let i = 0; i + 1 < path.length; i++) {
            this.toggleSegment(path[i], path[i + 1]);
        }
    }

    getStatus(): string {
        if (!this.isStarted()) {
            return 'Click a rim edge to place the stem, then add branches.';
        }

        if (this.checkWin()) {
            return 'You win! All digits at increasing distances!';
        }

        const boldRed = (t: string) => `<span style="color:#e94560;font-weight:bold">${t}</span>`;
        const boldAmber = (t: string) => `<span style="color:#fbbf24;font-weight:bold">${t}</span>`;

        const plural = (n: number, one: string, many: string = `${one}s`) =>
            `${n} ${n > 1 ? many : one}`;

        // A branching violation breaks the tree structure itself, so it outranks
        // the leftover-segment warnings below.
        const violations = this.branchingViolations.size;
        if (violations > 0) {
            return `${boldRed('Invalid')}: ${plural(violations, 'branch point')}. Branching only at digits.`;
        }

        const dangling = this.getDanglingEndpoints();
        if (dangling.length > 0) {
            return `${boldRed('Invalid')}: ${plural(dangling.length, 'dangling branch', 'dangling branches')}. Remove extra segments.`;
        }

        const correctDigits = this.getCorrectDigits();
        const digitCount = this.grid.maxDigit - this.grid.minDigit + 1;
        if (correctDigits.length === digitCount && this.disconnectedSegments.size > 0) {
            return `${boldRed('Invalid')}: ${plural(this.disconnectedSegments.size, 'disconnected segment')}. Remove stray segments.`;
        }

        if (correctDigits.length === 0) {
            if (!this.stem) {
                return 'Place a stem on the rim to start.';
            }
            return `Connect digit ${this.grid.minDigit} to the stem.`;
        }

        const outOfOrder = this.getOutOfOrderDigits();
        if (outOfOrder) {
            const [previous, next] = outOfOrder;
            return `${boldAmber('Warning')}: digit ${previous} should be reached before digit ${next}.`;
        }

        const nextDigit = correctDigits[correctDigits.length - 1] + 1;
        if (nextDigit <= this.grid.maxDigit) {
            return `Click cell edges to connect digit ${nextDigit} next.`;
        }

        return 'All digits are at increasing distances.';
    }

    /**
     * Serialize game state to string format.
     * Format: "T5,A0A1,A1B1" - stem (side letter + index), then segment edges.
     * Side letters: T=top, B=bottom, L=left, R=right
     */
    serialize(): string {
        const parts: string[] = [];

        // Serialize stem
        if (this.stem) {
            const sideChar = this.stem.side[0].toUpperCase(); // T, B, L, R
            parts.push(`${sideChar}${this.stem.index}`);
        }

        // Serialize segments
        for (const segKey of this.segments) {
            const edge = this.grid.deserializeEdge(segKey);
            if (edge) {
                parts.push(this.gs.serializeEdgeCells(edge));
            }
        }

        return parts.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        const compact = stripSeparators(serialized);
        let i = 0;
        // An optional stem makes the length ≡ 2 (mod 4); edges are 4 chars each.
        if (compact.length % 4 === 2) {
            const m = /^([TBLR])(\d)$/.exec(compact.slice(0, 2));
            if (m) {
                const sideMap: Record<string, 'top' | 'bottom' | 'left' | 'right'> = {
                    'T': 'top', 'B': 'bottom', 'L': 'left', 'R': 'right'
                };
                this.stem = { side: sideMap[m[1]], index: parseInt(m[2], 10) };
            }
            i = 2;
        }
        for (; i + 4 <= compact.length; i += 4) {
            const edge = this.grid.deserializeEdgeCells(compact.slice(i, i + 4));
            if (edge) {
                this.segments.add(edgeKey(edge));
            }
        }

        this.computeState();
    }
}
